import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateCategoryName } from '@/utils/contentTranslation';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuSub, 
  DropdownMenuSubContent, 
  DropdownMenuSubTrigger, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';

interface Category {
  id: string;
  name: string;
  image?: string;
  parent_id?: string | null;
}

interface CategoriesComponentProps {
  isMobile?: boolean;
  onItemClick?: () => void;
}

const HeaderCategories = ({ isMobile = false, onItemClick }: CategoriesComponentProps) => {
  const { t, currentLanguage } = useLanguage();
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<{[key: string]: Category[]}>({});
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        
        // Buscar categorias principais (sem parent_id)
        const { data: mainCats, error: mainError } = await supabase
          .from('categories')
          .select('*')
          .is('parent_id', null);
          
        if (mainError) throw mainError;
        
        // Buscar todas as subcategorias
        const { data: subCats, error: subError } = await supabase
          .from('categories')
          .select('*')
          .not('parent_id', 'is', null);
          
        if (subError) throw subError;
        
        // Organizar subcategorias por parent_id
        const subcatsMap: {[key: string]: Category[]} = {};
        subCats?.forEach(subcat => {
          if (subcat.parent_id) {
            if (!subcatsMap[subcat.parent_id]) {
              subcatsMap[subcat.parent_id] = [];
            }
            subcatsMap[subcat.parent_id].push(subcat);
          }
        });
        
        setMainCategories(mainCats || []);
        setSubcategories(subcatsMap);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, []);

  // Se estiver carregando, mostrar categorias padrão temporárias
  if (loading) {
    return isMobile ? (
      <div className="py-2">
        <div className="flex items-center justify-between mb-2">
          <Link to="/shop" className="hover:text-brand-red" onClick={onItemClick}>{t('header.products')}</Link>
        </div>
        <div className="pl-4 space-y-2">
          <div className="animate-pulse h-6 bg-gray-700 rounded w-24 mb-2"></div>
          <div className="animate-pulse h-6 bg-gray-700 rounded w-24 mb-2"></div>
          <div className="animate-pulse h-6 bg-gray-700 rounded w-24"></div>
        </div>
      </div>) : (
      <ul>
        <li className="relative group">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="hover:text-brand-red font-medium flex items-center">
                {t('header.products')}
                <ChevronDown className="h-4 w-4 ml-1" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700">
              <div className="p-2">
                <div className="animate-pulse h-6 bg-gray-700 rounded w-full mb-2"></div>
                <div className="animate-pulse h-6 bg-gray-700 rounded w-full mb-2"></div>
                <div className="animate-pulse h-6 bg-gray-700 rounded w-full"></div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </li>
      </ul>
    );
  }

  // Se não houver categorias, mostrar link simples
  if (mainCategories.length === 0) {
    return (
      <li>
        <Link to="/shop" className="hover:text-brand-red font-medium" onClick={onItemClick}>
          {t('categories.allProducts')}
        </Link>
      </li>
    );
  }

  // Versão mobile
  if (isMobile) {
    const toggleCategory = (categoryId: string) => {
      setExpandedCategories(prev => ({
        ...prev,
        [categoryId]: !prev[categoryId],
      }));
    };

    return (
      <div className="py-2">
        <div className="flex items-center justify-between mb-2">
          <Link to="/shop" className="hover:text-brand-red" onClick={onItemClick}>
            {t('header.products')}
          </Link>
        </div>
        <ul className="pl-4 space-y-2">
          {mainCategories.map((category) => (
            <li key={category.id}>
              <div className="flex items-center justify-between mb-1">
                <Link
                  to={`/shop?category=${category.id}`}
                  className="text-gray-300 hover:text-brand-red text-sm flex-1"
                  onClick={onItemClick}
                >
                  {translateCategoryName(category.name, currentLanguage)}
                </Link>
                {subcategories[category.id] && subcategories[category.id].length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    aria-expanded={!!expandedCategories[category.id]}
                    aria-controls={`subcategory-panel-${category.id}`}
                    className="ml-2 p-1 text-gray-400 hover:text-brand-red transition-transform"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transform transition-transform ${expandedCategories[category.id] ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}
              </div>
              {subcategories[category.id] && subcategories[category.id].length > 0 && expandedCategories[category.id] && (
                <ul
                  id={`subcategory-panel-${category.id}`}
                  className="pl-3 space-y-1 mt-1 border-l border-gray-700/60"
                >
                  {subcategories[category.id].map((subcat) => (
                    <li key={subcat.id}>
                      <Link 
                        to={`/shop?subcategory=${subcat.id}`} 
                        className="text-gray-400 hover:text-brand-red text-xs"
                        onClick={onItemClick}
                      >
                        {translateCategoryName(subcat.name, currentLanguage)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Versão desktop com dropdown
  return (
    <ul>
      <li className="relative group">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hover:text-brand-red font-medium flex items-center">
              {t('header.products')}
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700">
            {mainCategories.map((category) => (
              <div key={category.id} className="relative">
                {subcategories[category.id] && subcategories[category.id].length > 0 ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="hover:bg-gray-800 text-gray-200 hover:text-white">
                      {translateCategoryName(category.name, currentLanguage)}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="bg-gray-900 border-gray-700">
                      {subcategories[category.id].map((subcategory) => (
                        <DropdownMenuItem 
                          key={subcategory.id}
                          asChild
                          className="hover:bg-gray-800 text-gray-200 hover:text-white"
                        >
                          <Link to={`/shop?subcategory=${subcategory.id}`}>
                            {translateCategoryName(subcategory.name, currentLanguage)}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator className="bg-gray-700" />
                      <DropdownMenuItem 
                        asChild 
                        className="hover:bg-gray-800 text-gray-200 hover:text-white"
                      >
                        <Link to={`/shop?category=${category.id}`}>
                          {t('categories.viewAllIn')} {translateCategoryName(category.name, currentLanguage)}
                        </Link>
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : (
                  <DropdownMenuItem
                  asChild
                  className="hover:bg-gray-800 text-gray-200 hover:text-white"
                >
                  <Link to={`/shop?category=${category.id}`}>
                    {translateCategoryName(category.name, currentLanguage)}
                  </Link>
                </DropdownMenuItem>
                )}
              </div>
            ))}
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuItem 
              asChild 
              className="hover:bg-gray-800 text-gray-200 hover:text-white"
            >
              <Link to="/shop">{t('categories.viewAllProducts')}</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    </ul>
  );
};

export default HeaderCategories;
